import type { SessionSummary } from '@/api/types';

export type DisplayStatus = 'starting' | 'on' | 'running' | 'stopping' | 'off';

export interface AgentInfo {
  id: string;
  name: string;
  status: DisplayStatus;
  uptime: string | null;
  pinned: boolean;
  lastInteractionMs: number | null;
}

export const formatUptime = (ms: number) => {
  const clamped = Math.max(ms, 0);
  const days = Math.floor(clamped / 86_400_000);
  const hours = Math.floor((clamped % 86_400_000) / 3_600_000);
  const mins = Math.floor((clamped % 3_600_000) / 60_000);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

export const sessionToAgent = (session: SessionSummary): AgentInfo => {
  const serverStatus = session.runtime_status;

  let displayStatus: DisplayStatus;
  if (serverStatus === 'on') {
    displayStatus = session.active_run_id ? 'running' : 'on';
  } else {
    displayStatus = serverStatus;
  }

  let uptime: string | null = null;
  if ((displayStatus === 'on' || displayStatus === 'running') && session.runtime_started_at) {
    const wallMs = Date.now() - new Date(session.runtime_started_at).getTime();
    uptime = formatUptime(wallMs);
  }

  return {
    id: session.id,
    name: session.title || 'Untitled Agent',
    status: displayStatus,
    uptime,
    pinned: session.pinned,
    lastInteractionMs: session.last_interaction_at?.valueOf() ?? null,
  };
};

export const formatTimeAgo = (ms: number | null): string => {
  if (ms == null) return '—';
  const delta = Math.max(Date.now() - ms, 0);
  const secs = Math.floor(delta / 1000);
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(months / 12);
  return `${years}y ago`;
};
